import { css } from '@emotion/react'
import { HFlow, Text, Tooltip, VFlow } from 'bold-ui'
import { DateTime } from 'components/date'
import { DateField, ErrorField, NumberField, RadioField } from 'components/form'
import { usePecField } from 'components/form/core/hooks/useField'
import { UnidadeMedidaTempoEnum } from 'graphql/types.generated'
import { MetaRoot } from 'util/metaPath'

import { MedicamentoFormModel, TipoDoseEnum } from '../../../model-prescricao'

interface DuracaoTratamentoSectionProps {
  name: MetaRoot<MedicamentoFormModel>
  dataAtendimento: Date
  onEscalaDuracaoChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  dataFimTratamento?: Date
}

const DOSE_UNICA_TOOLTIP_TEXT = 'Medicamento definido como dose única'

export function DuracaoTratamentoSection(props: Readonly<DuracaoTratamentoSectionProps>) {
  const { name, dataAtendimento, onEscalaDuracaoChange, dataFimTratamento } = props

  const { input: tipoDoseInput } = usePecField({ name: name.tipoDose, subscription: { value: true } })
  const tipoDose = tipoDoseInput.value

  const { input: usoContinuoInput } = usePecField({ name: name.usoContinuo, subscription: { value: true } })
  const usoContinuo = usoContinuoInput.value

  const { input: duracaoInput } = usePecField({ name: name.duracao, subscription: { value: true } })
  const duracao = duracaoInput.value

  const { input: escalaDuracaoInput } = usePecField({ name: name.escalaDuracao, subscription: { value: true } })
  const escalaDuracao = escalaDuracaoInput.value

  const isDoseUnica = tipoDose === TipoDoseEnum.UNICA
  const isDisabled = isDoseUnica || usoContinuo
  const showDataFim = duracao && escalaDuracao !== UnidadeMedidaTempoEnum.INDETERMINADO && dataFimTratamento

  return (
    <HFlow>
      <div data-cy='InicioTratamento' css={styles.inicioTratamentoDiv}>
        <DateField
          name={name.dataInicioTratamento}
          label='Início do tratamento'
          minDate={dataAtendimento}
          required
          data-cy='InicioTratamentoDateField'
        />
      </div>
      <div data-cy='Duracao' css={styles.duracaoDiv}>
        <Tooltip text={isDoseUnica && DOSE_UNICA_TOOLTIP_TEXT}>
          <NumberField
            name={name.duracao}
            label='Duração'
            maxLength={3}
            disabled={escalaDuracao === UnidadeMedidaTempoEnum.INDETERMINADO || isDoseUnica}
            required={tipoDose !== TipoDoseEnum.UNICA}
            data-cy='DuracaoTextField'
          />
        </Tooltip>
      </div>
      <VFlow>
        <Tooltip text={isDoseUnica && DOSE_UNICA_TOOLTIP_TEXT}>
          <HFlow>
            <VFlow>
              <RadioField
                name={name.escalaDuracao}
                value={UnidadeMedidaTempoEnum.DIAS}
                label='Dia(s)'
                disabled={isDisabled}
                data-cy='DuracaoDiasRadioButton'
              />
              <RadioField
                name={name.escalaDuracao}
                value={UnidadeMedidaTempoEnum.MESES}
                label='Mês(es)'
                disabled={isDisabled}
                data-cy='DuracaoMesesRadioButton'
              />
            </VFlow>
            <VFlow>
              <RadioField
                name={name.escalaDuracao}
                value={UnidadeMedidaTempoEnum.SEMANAS}
                label='Semana(s)'
                disabled={isDisabled}
                data-cy='DuracaoSemanasRadioButton'
              />
              <RadioField
                name={name.escalaDuracao}
                value={UnidadeMedidaTempoEnum.INDETERMINADO}
                label='Indeterminada'
                disabled={isDisabled}
                onChange={onEscalaDuracaoChange}
                data-cy='DuracaoIndeterminadoRadioButton'
              />
            </VFlow>
          </HFlow>
        </Tooltip>
        <ErrorField name={name.escalaDuracao} />
      </VFlow>
      <VFlow vSpacing={0} data-cy='ConclusaoLabel'>
        <Text fontWeight='bold'>Conclusão</Text>
        {showDataFim ? <DateTime value={dataFimTratamento} format='DD/MM/YYYY' /> : <Text>-</Text>}
      </VFlow>
    </HFlow>
  )
}

const styles = {
  inicioTratamentoDiv: css`
    max-width: 9.625rem;
  `,
  duracaoDiv: css`
    max-width: 6rem;
  `,
}
